import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  MenuItem,
  Alert,
  TextField,
  Button,
  Stack,
  Divider,
} from "@mui/material";

import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

import {
  useGetDeputyReportSummaryTwoQuery,
  useLazyGetDeputyReportSummaryTwoQuery,
} from "../../../services/report.query";
import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { MRT_SortingState } from "material-react-table";
import { exportDeputyReportSummaryExcel } from "./deputy-report-summary-excel";
import { useTableState } from "../../../hooks/useTableState";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { Notify } from "../../../layouts/notify";
import {
  fetchAllInBatches,
  mapRowsInChunks,
  yieldToMainThread,
} from "../../../utils/exportUtils";

export default function DeputyReportSummaryPage() {
  const currentYear = new Date().getFullYear();

  const defaultFilters = useMemo(
    () => ({
      year: String(currentYear),
      search: "",
      endorser: "",
      startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    }),
    [currentYear]
  );

  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 25,
    defaultSortBy: "total",
    defaultSortOrder: "desc",
  });

  const [draftYear, setDraftYear] = useState(filters.year);
  const [draftSearch, setDraftSearch] = useState(filters.search);
  const [draftEndorser, setDraftEndorser] = useState(filters.endorser);
  const [draftStartDate, setDraftStartDate] = useState<Dayjs | null>(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [draftEndDate, setDraftEndDate] = useState<Dayjs | null>(
    filters.endDate ? dayjs(filters.endDate) : null
  );

  useEffect(() => {
    setDraftYear(filters.year);
    setDraftSearch(filters.search);
    setDraftEndorser(filters.endorser);
    setDraftStartDate(filters.startDate ? dayjs(filters.startDate) : null);
    setDraftEndDate(filters.endDate ? dayjs(filters.endDate) : null);
  }, [filters]);

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const { data = [], isLoading, isFetching, isError } =
    useGetDeputyReportSummaryTwoQuery(
      {
        p_start_date: filters.startDate || null,
        p_end_date: filters.endDate || null,
        p_year: Number(filters.year) || null,
        p_search: filters.search?.trim() ? filters.search.trim() : null,
        p_endorser: filters.endorser?.trim() ? filters.endorser.trim() : null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_sort_column: sortBy || "total",
        p_sort_direction: sortOrder || "desc",
      },
      { refetchOnMountOrArgChange: true }
    );

  const totalCount = data.length > 0 ? data[0].total_count : 0;

  const handleSearch = () => {
    setFilters({
      year: draftYear,
      search: draftSearch.trim(),
      endorser: draftEndorser.trim(),
      startDate: draftStartDate ? draftStartDate.format("YYYY-MM-DD") : "",
      endDate: draftEndDate ? draftEndDate.format("YYYY-MM-DD") : "",
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const [triggerFetchAllSummaries] = useLazyGetDeputyReportSummaryTwoQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportProgress, setExportProgress] = useState<number | null>(null);

  const fetchAllDeputySummaries = async () => {
    return fetchAllInBatches<any>({
      batchSize: 1000,
      knownTotal: Number(totalCount || 0),
      fetchBatch: async (offset, limit) => {
        const chunk = await triggerFetchAllSummaries(
          {
            p_start_date: filters.startDate || null,
            p_end_date: filters.endDate || null,
            p_year: Number(filters.year) || null,
            p_search: filters.search?.trim() ? filters.search.trim() : null,
            p_endorser: filters.endorser?.trim() ? filters.endorser.trim() : null,
            p_limit: limit,
            p_offset: offset,
            p_sort_column: sortBy || "total",
            p_sort_direction: sortOrder || "desc",
          },
          false
        ).unwrap();
        const rows = chunk || [];
        const batchTotal =
          rows.length > 0 && rows[0].total_count
            ? Number(rows[0].total_count)
            : Number(totalCount || 0);
        return { rows, total: batchTotal };
      },
      onProgress: (fetched, total, percent) => {
        setExportProgress(percent);
        if (total && total > 0) {
          setExportMessage(
            `Fetching deputy summaries: ${fetched.toLocaleString()} of ${total.toLocaleString()} deputies...`
          );
        } else {
          setExportMessage(
            `Fetching deputy summaries: ${fetched.toLocaleString()} deputies loaded...`
          );
        }
      },
    });
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching deputy report summaries...");
    try {
      const allRows = await fetchAllDeputySummaries();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(
        `Formatting ${allRows.length.toLocaleString()} deputy records for PDF...`
      );
      await yieldToMainThread(30);

      const tableRows = await mapRowsInChunks(
        allRows,
        (row: any, idx: number) => [
          idx + 1,
          String(row.control_number || "—"),
          `${row.lname || ""}, ${row.fname || ""}`.trim() || "—",
          String(row.total ?? 0),
          String(row.endorser || "—"),
        ],
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing PDF rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(
        `Rendering official PDF document (${allRows.length.toLocaleString()} deputies)...`
      );
      await yieldToMainThread(40);

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text("Deputy Report Summary Directory", 14, 21);

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      const dateRangeText = `Date Range: ${filters.startDate || "Beginning"} to ${filters.endDate || "Present"}`;
      const endorserText = filters.endorser?.trim()
        ? `  |  Endorser: ${filters.endorser.trim()}`
        : "";
      const searchText = filters.search?.trim()
        ? `  |  Search: ${filters.search.trim()}`
        : "";
      doc.text(
        `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  Year Registered: ${filters.year}  |  ${dateRangeText}${endorserText}${searchText}  |  Total Deputies: ${allRows.length.toLocaleString()}`,
        14,
        27
      );

      const tableColumn = ["#", "Control No.", "Deputy Name", "Total Reports", "Endorser"];

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 16, halign: "center" },
          1: { cellWidth: 38 },
          2: { cellWidth: 85 },
          3: { cellWidth: 32, halign: "right" },
          4: { cellWidth: "auto" },
        },
        margin: { top: 12, left: 14, right: 14, bottom: 14 },
      });

      // Stamp total page count across all pages after table completes
      const totalPages = (doc as any).internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - 14,
          pageHeight - 6,
          { align: "right" }
        );
      }

      setExportProgress(100);
      setExportMessage("Download starting...");
      await yieldToMainThread(20);

      doc.save(`MTRCB_Deputy_Report_Summary_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify(
        "success",
        `Exported all ${allRows.length.toLocaleString()} deputy report summaries to PDF.`
      );
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching deputy report summaries...");
    try {
      const allRows = await fetchAllDeputySummaries();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(92);
      setExportMessage(
        `Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} deputies...`
      );
      await yieldToMainThread(40);

      exportDeputyReportSummaryExcel(allRows, {
        year: Number(filters.year),
        search: filters.search,
        endorser: filters.endorser || null,
        dateRange: [filters.startDate || "—", filters.endDate || "—"],
      });

      setExportProgress(100);
      setExportMessage("Download complete!");
      await yieldToMainThread(20);

      Notify(
        "success",
        `Exported all ${allRows.length.toLocaleString()} deputy report summaries to Excel.`
      );
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 60,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            {(page - 1) * pageSize + row.index + 1}
          </Typography>
        ),
      },
      {
        accessorKey: "control_number",
        header: "Control Number",
        size: 140,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "deputy",
        header: "Deputy",
        size: 300,
        Cell: ({ row }: any) => (
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              src={
                row.original.profile_picture ||
                "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png"
              }
              sx={{ width: 32, height: 32, fontSize: 13, border: "1px solid #E2E8F0" }}
            >
              {row.original.lname?.charAt(0) || "U"}
            </Avatar>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
              {row.original.lname}, {row.original.fname}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "total",
        header: "Total Reports",
        size: 140,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0072BC" }}>
            {Number(cell.getValue() || 0).toLocaleString()}
          </Typography>
        ),
      },
      {
        accessorKey: "endorser",
        header: "Endorser",
        size: 260,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DefaultPage
        title="Deputy Report Summary"
        subTitle="Overview of total reports submitted by deputies across all categories."
      >
        <Box mb={2}>
          <Alert severity="info" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Reports must be tagged as <strong>ACKNOWLEDGED</strong> to be included in count totals.
          </Alert>
        </Box>

        {isError && (
          <Box mb={2}>
            <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
              Failed to load deputy report summary.
            </Alert>
          </Box>
        )}

        {/* Standardized Gold-Standard Filter Panel */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "6px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <DatePicker
                label="From"
                value={draftStartDate}
                onChange={(v) => setDraftStartDate(v)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 160,
                      "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                    },
                  },
                }}
              />

              <DatePicker
                label="To"
                value={draftEndDate}
                onChange={(v) => setDraftEndDate(v)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 160,
                      "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                    },
                  },
                }}
              />

              <TextField
                select
                size="small"
                label="Year Registered"
                value={draftYear}
                onChange={(e) => setDraftYear(e.target.value)}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              >
                {Array.from({ length: currentYear - 2024 }, (_, i) => 2025 + i).map((y) => (
                  <MenuItem key={y} value={String(y)}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Endorser"
                placeholder="Search endorser..."
                value={draftEndorser}
                onChange={(e) => setDraftEndorser(e.target.value)}
                onKeyDown={handleEnterSearch}
                sx={{
                  minWidth: 190,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                size="small"
                label="Deputy / Control No."
                placeholder="Search deputy..."
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
                onKeyDown={handleEnterSearch}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />
            </Stack>

            <Divider sx={{ borderColor: "#F1F5F9" }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchRounded fontSize="small" />}
                  onClick={handleSearch}
                  disabled={isFetching}
                  sx={{
                    backgroundColor: "#0072BC",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: 13,
                    px: 2,
                    py: 0.75,
                    borderRadius: "4px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#005a96",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isFetching ? "Searching..." : "Search"}
                </Button>

                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltRounded fontSize="small" />}
                    onClick={handleClear}
                    disabled={isFetching}
                    sx={{
                      color: "#64748B",
                      borderColor: "#CBD5E1",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: "4px",
                      "&:hover": {
                        borderColor: "#94A3B8",
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfRounded fontSize="small" />}
                  onClick={handleExportPDF}
                  disabled={isExporting || totalCount === 0}
                  sx={{
                    color: "#334155",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Export PDF
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChartRounded fontSize="small" />}
                  onClick={handleExportExcel}
                  disabled={isExporting || totalCount === 0}
                  sx={{
                    color: "#0072BC",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#0072BC",
                      backgroundColor: "#F0F9FF",
                    },
                  }}
                >
                  Export Excel
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {/* Matched Records Feedback */}
        <Box mb={2}>
          <Alert
            severity="success"
            sx={{
              borderRadius: "6px",
              fontSize: 13,
              border: "1px solid #BBF7D0",
              backgroundColor: "#F0FDF4",
              py: 0.5,
              "& .MuiAlert-icon": {
                color: "#00934C",
              },
            }}
          >
            Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
          </Alert>
        </Box>

        <DataTableFive
          data={data}
          total={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          columns={columns as any}
          getRowId={(row: any) => row.control_number}
          isLoading={isLoading}
          isFetching={isFetching}
          sorting={sorting}
          onSortingChange={(newSorting) => {
            if (newSorting.length > 0) {
              updateTableSorting(newSorting[0].id, newSorting[0].desc ? "desc" : "asc");
            }
          }}
        />

        <ExportLoadingBackdrop
          open={isExporting}
          message={exportMessage}
          progress={exportProgress}
        />
      </DefaultPage>
    </LocalizationProvider>
  );
}